import { assign } from 'xstate';
import logger from '../../../logger';
import { renameNotebookCloud, createNotebookCloud } from '../../cloud/notebooks';
import { notebookTreeHelper } from '../../../utils/helpers';
import type { AppStateContext } from '../../../types/appState';

// ---------------------------------------------------------------------------
// Service functions
// ---------------------------------------------------------------------------

async function renameNotebookService(context: AppStateContext, event: any) {
  const { notebookId, newNotebookName } = event.data;
  const savedNotebook = await renameNotebookCloud({
    notebook_id: notebookId,
    notebook_name: newNotebookName,
  });
  return { savedNotebook };
}

async function createNotebookService(context: AppStateContext, event: any) {
  const { notebookName } = event.data;
  logger.info(`Invoking createNotebookCloud with name: ${notebookName}`);
  const newNotebook = await createNotebookCloud({ notebook_name: notebookName });
  return { newNotebook };
}

// ---------------------------------------------------------------------------
// Idle event routing
// ---------------------------------------------------------------------------

export const notebooksIdleOn = {
  selectNotebook: { target: 'selectingNotebook' },
  renameNotebook: { target: 'renamingNotebook' },
  createNotebook: { target: 'creatingNotebook' },
};

// ---------------------------------------------------------------------------
// State definitions
// ---------------------------------------------------------------------------

export const notebooksStates = {
  selectingNotebook: {
    id: 'selectingNotebook',
    entry: [assign<AppStateContext, any>((context, event) => {
      logger.info({ context, event }, 'selectingNotebook data');
      const { notebooks, notes } = context;
      const { notebook } = event.data;
      const { notebookTree, notebookTreeList } = notebookTreeHelper({
        notebooks,
        notebookId: notebook.notebook_id,
        notes,
      });
      return {
        selectedNotebook: notebook,
        selectedNotebookId: notebook.notebook_id,
        notebookTree,
        notebookTreeList,
        showNoteSearch: false,
      };
    })],
    always: [
      {
        target: '#idle',
        cond: (context: AppStateContext) => !!(context.selectedNotebook && Object.keys(context.selectedNotebook).length),
      },
      { target: '#idle' },
    ],
  },

  renamingNotebook: {
    id: 'renamingNotebook',
    initial: 'savingRenamedNotebook',
    states: {
      savingRenamedNotebook: {
        entry: [(context: any, event: any) => {
          logger.debug({ context, event }, 'Saving notebook rename');
        }],
        invoke: {
          src: renameNotebookService,
          onDone: {
            target: '#idle',
            actions: assign<AppStateContext, any>((context, event) => {
              const { notebooks } = context;
              const { savedNotebook } = event.data;
              const updatedNotebooks = { ...notebooks, [savedNotebook.notebook_id]: savedNotebook };
              const notebooksList = Object.values(updatedNotebooks) as import('../../../types/notebooks').Notebook[];
              const { notebookTree, notebookTreeList } = notebookTreeHelper({
                notebooks: updatedNotebooks,
                notebookId: context.selectedNotebookId,
                notes: context.notes,
              });
              return {
                notebooks: updatedNotebooks,
                notebooksIterable: notebooksList,
                notebookTree,
                notebookTreeList,
              };
            }),
          },
          onError: {
            target: '#renamingNotebookFailure',
            actions: (context: any, event: any) => {
              logger.error({ error: event.data }, 'Failed to rename notebook');
            },
          },
        },
      },
    },
  },

  renamingNotebookFailure: {
    id: 'renamingNotebookFailure',
    entry: [(context: any, event: any) => {
      logger.info({ context, event }, 'renamingNotebookFailure data');
    }],
    always: { target: '#idle' },
  },

  creatingNotebook: {
    id: 'creatingNotebook',
    initial: 'savingNewNotebook',
    states: {
      savingNewNotebook: {
        entry: [(context: any, event: any) => {
          logger.debug({ context, event }, 'Saving new notebook');
        }],
        invoke: {
          id: 'invokeCreateNotebookCloud',
          src: createNotebookService,
          onDone: {
            target: '#idle',
            actions: assign<AppStateContext, any>((context, event) => {
              const { notebooks } = context;
              const { newNotebook } = event.data;
              logger.info({ newNotebook }, 'createNotebookCloud successful, updating context');
              const updatedNotebooks = { ...notebooks, [newNotebook.notebook_id]: newNotebook };
              const notebooksList = Object.values(updatedNotebooks) as import('../../../types/notebooks').Notebook[];
              const { notebookTree, notebookTreeList } = notebookTreeHelper({
                notebooks: updatedNotebooks,
                notebookId: context.selectedNotebookId,
                notes: context.notes,
              });
              return {
                notebooks: updatedNotebooks,
                notebooksIterable: notebooksList,
                notebookTree,
                notebookTreeList,
              };
            }),
          },
          onError: {
            target: '#creatingNotebookFailure',
            actions: (context: any, event: any) => {
              logger.error({ error: event.data }, 'Failed to create notebook');
            },
          },
        },
      },
    },
  },

  creatingNotebookFailure: {
    id: 'creatingNotebookFailure',
    entry: [(context: any, event: any) => {
      logger.error({ context, event }, 'creatingNotebookFailure data');
    }],
    always: { target: '#idle' },
  },
};
